import { useState, useMemo } from 'react'
import { Card, Col, Row, Badge, ProgressBar, Button, Form, Spinner, Table, Alert } from 'react-bootstrap'
import { useQuery } from '@apollo/client'
import { GET_DIFY_USAGE_REPORT } from '../../gql/queries/difyQueries'
import { useGetUser } from '../../store/userStore'
import { parseId } from '../../helpers'
import axios from 'axios'
import { toast } from 'react-toastify'

export const DifyUsageReportPage = () => {
  const [days, setDays] = useState(30)
  const user = useGetUser((s) => s.user)
  const organizationId = user?.user?.organization?.id
  const decodedOrgId = organizationId ? String(parseId(organizationId)) : ''

  const { data, loading, error, refetch } = useQuery(GET_DIFY_USAGE_REPORT, {
    variables: { days, organizationId: decodedOrgId },
    skip: !decodedOrgId,
  })

  const report = data?.difyUsageReport || {}

  const handleExportCSV = async () => {
    try {
      const response = await axios.get('/api/chat/dify-usage-report/export/', {
        params: { days, org_id: decodedOrgId },
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `dify-usage-${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      link.parentElement?.removeChild(link)
      toast.success('CSV exportado exitosamente')
    } catch (err) {
      toast.error('Error al exportar CSV')
    }
  }

  if (loading) {
    return (
      <div className="container-fluid pt-5 text-center">
        <Spinner animation="border" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container-fluid pt-5">
        <Alert variant="danger">
          Error al cargar reportes: {error.message}
        </Alert>
      </div>
    )
  }

  const successRate = report.success_rate || 0

  return (
    <div className="container-fluid pt-5 pb-5">
      {/* Header */}
      <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h2 className="mb-1">📊 Consumo de Dify</h2>
          <small className="text-muted">Auditoría y analytics de uso de Dify AI</small>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Form.Select
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value))
              refetch({ days: Number(e.target.value), organizationId: decodedOrgId })
            }}
            style={{ width: '200px' }}
          >
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </Form.Select>
          <Button variant="outline-primary" onClick={handleExportCSV}>
            📥 Exportar CSV
          </Button>
        </div>
      </div>

      {/* Resumen Cards */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-2">📊 Total Mensajes</h6>
                  <h2 className="text-primary mb-2">
                    {report.total_calls?.toLocaleString() || '0'}
                  </h2>
                  <ProgressBar
                    now={successRate}
                    variant={successRate >= 95 ? 'success' : successRate >= 80 ? 'warning' : 'danger'}
                  />
                  <small className="text-muted d-block mt-2">
                    {report.successful_calls || '0'} exitosos ({successRate}%)
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div>
                <h6 className="text-muted mb-2">💾 Tokens Consumidos</h6>
                <h2 className="text-info mb-2">
                  {report.total_tokens?.toLocaleString() || '0'}
                </h2>
                <small className="text-muted d-block">
                  📥 Entrada: {report.input_tokens?.toLocaleString() || '0'}
                </small>
                <small className="text-muted d-block">
                  📤 Salida: {report.output_tokens?.toLocaleString() || '0'}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div>
                <h6 className="text-muted mb-2">⏱️ Latencia Promedio</h6>
                <h2 className="text-warning mb-2">
                  {report.avg_latency_ms?.toFixed(0) || '0'}ms
                </h2>
                <small className="text-muted d-block">
                  ❌ Errores: {report.error_calls || '0'}
                </small>
                <small className="text-muted d-block">
                  ⏳ Timeouts: {report.timeout_calls || '0'}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div>
                <h6 className="text-muted mb-2">📈 Promedio/Mensaje</h6>
                <h2 className="text-success mb-2">
                  {report.total_calls > 0
                    ? ((report.total_tokens || 0) / (report.total_calls || 1)).toFixed(1)
                    : '0'}
                </h2>
                <small className="text-muted d-block">tokens por mensaje</small>
                <div className="mt-3">
                  <Badge bg="info" className="me-1">Período: {days} días</Badge>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Por Agente */}
      {report.by_agent && report.by_agent.length > 0 && (
        <Row className="mb-4">
          <Col lg={6}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-light">
                <h5 className="mb-0">👥 Consumo por Agente</h5>
              </Card.Header>
              <Card.Body>
                {report.by_agent.map((agent: any) => (
                  <div key={agent.agent_id} className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold">
                        {agent.agent_name || `Agente ${agent.agent_id}`}
                      </span>
                      <Badge bg="info">{agent.calls} msgs</Badge>
                    </div>
                    <ProgressBar
                      now={(agent.calls / (report.total_calls || 1)) * 100}
                      variant="info"
                      className="mb-2"
                    />
                    <small className="text-muted d-block">
                      {agent.tokens?.toLocaleString() || 0} tokens | {agent.avg_latency_ms}ms promedio
                    </small>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-light">
                <h5 className="mb-0">📊 Distribución por Status</h5>
              </Card.Header>
              <Card.Body>
                <div>
                  {report.by_status &&
                    Object.entries(report.by_status).map(([status, data]: any) => (
                      <div key={status} className="mb-3">
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            {status === 'success' && '✅ Éxito'}
                            {status === 'error' && '❌ Error'}
                            {status === 'timeout' && '⏳ Timeout'}
                          </span>
                          <Badge
                            bg={
                              status === 'success'
                                ? 'success'
                                : status === 'error'
                                  ? 'danger'
                                  : 'warning'
                            }
                          >
                            {data.count} ({((data.count / (report.total_calls || 1)) * 100).toFixed(1)}%)
                          </Badge>
                        </div>
                        <ProgressBar
                          now={(data.count / (report.total_calls || 1)) * 100}
                          variant={
                            status === 'success'
                              ? 'success'
                              : status === 'error'
                                ? 'danger'
                                : 'warning'
                          }
                        />
                      </div>
                    ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabla de Logs Recientes */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">📋 Últimos Mensajes Procesados</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover className="mb-0">
            <thead>
              <tr className="bg-light">
                <th>Fecha/Hora</th>
                <th>Estado</th>
                <th>Entrada</th>
                <th>Salida</th>
                <th>Total</th>
                <th>Latencia</th>
              </tr>
            </thead>
            <tbody>
              {report.recent_logs && report.recent_logs.length > 0 ? (
                report.recent_logs.map((log: any) => (
                  <tr key={log.id}>
                    <td>
                      <small>
                        {new Date(log.created).toLocaleString()}
                      </small>
                    </td>
                    <td>
                      <Badge
                        bg={
                          log.status === 'success'
                            ? 'success'
                            : log.status === 'error'
                              ? 'danger'
                              : 'warning'
                        }
                      >
                        {log.status}
                      </Badge>
                    </td>
                    <td>
                      <small>{log.input_tokens || '-'}</small>
                    </td>
                    <td>
                      <small>{log.output_tokens || '-'}</small>
                    </td>
                    <td>
                      <small className="fw-bold">{log.total_tokens || '-'}</small>
                    </td>
                    <td>
                      <small>{log.dify_latency_ms}ms</small>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No hay registros disponibles
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  )
}
